import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { getProductCompanyProfitReport, getRepresentativeRevenueReport, getTargets, getUsers, getDashboardReport, getRepDetails, getSalesQuantityReport } from '@/api/api';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, ComposedChart, Area } from 'recharts';
import { TrendingUp, DollarSign, Package, Users, Award, Target, Phone, MapPin, ShoppingCart, BarChart3, Download, ChevronDown, ChevronUp, Calendar, Filter, CheckCircle, AlertCircle, Clock, UserPlus, UserCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import PageHeader from '@/components/PageHeader';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';

const Reports = ({ user }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [representatives, setRepresentatives] = useState([]);
  const [selectedRepresentative, setSelectedRepresentative] = useState('all');
  const [allUsers, setAllUsers] = useState([]);
  
  // Hedefler
  const [targets, setTargets] = useState([]);
  
  // Performans verileri
  const [performanceData, setPerformanceData] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  
  // Satış detayları
  const [salesQuantityData, setSalesQuantityData] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState({});
  
  // Detay modal
  const [showRepDetailsDialog, setShowRepDetailsDialog] = useState(false);
  const [repDetailsData, setRepDetailsData] = useState({ type: '', rep: '', data: [] });
  
  // Detaylı satış modal
  const [showDetailedSalesModal, setShowDetailedSalesModal] = useState(false);
  const [detailedSalesData, setDetailedSalesData] = useState([]);
  const [detailedSalesLoading, setDetailedSalesLoading] = useState(false);
  
  // Yeni/Mevcut müşteri satış verileri
  const [customerSalesData, setCustomerSalesData] = useState({ newCustomers: 0, existingCustomers: 0, newCustomerSales: [], existingCustomerSales: [] });
  
  // Ciro ve kar verileri
  const [productProfitData, setProductProfitData] = useState([]);
  const [representativeRevenueData, setRepresentativeRevenueData] = useState([]);

  // Tarih filtreleri - Yeni tasarım
  const today = new Date();
  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  
  const [dateFilter, setDateFilter] = useState('thisMonth'); // thisMonth, lastMonth, allTime, custom
  const [customStartDate, setCustomStartDate] = useState(formatDate(new Date(today.getFullYear(), today.getMonth(), 1)));
  const [customEndDate, setCustomEndDate] = useState(formatDate(today));
  
  // Hesaplanan tarih aralığı
  const { startDate, endDate } = useMemo(() => {
    const now = new Date();
    switch (dateFilter) {
      case 'thisMonth':
        return {
          startDate: formatDate(new Date(now.getFullYear(), now.getMonth(), 1)),
          endDate: formatDate(now)
        };
      case 'lastMonth':
        const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);
        return {
          startDate: formatDate(lastMonth),
          endDate: formatDate(lastMonthEnd)
        };
      case 'allTime':
        return {
          startDate: '2020-01-01',
          endDate: formatDate(now)
        };
      case 'custom':
        return {
          startDate: customStartDate,
          endDate: customEndDate
        };
      default:
        return {
          startDate: formatDate(new Date(now.getFullYear(), now.getMonth(), 1)),
          endDate: formatDate(now)
        };
    }
  }, [dateFilter, customStartDate, customEndDate]);

  // Rol kontrolü - temsilci sadece kendini görebilir
  useEffect(() => {
    if (user?.role === 'temsilci') {
      setSelectedRepresentative(user.username);
    }
  }, [user]);

  useEffect(() => {
    loadRepresentatives();
    loadTargets();
  }, []);

  useEffect(() => {
    loadAllData();
  }, [startDate, endDate, selectedRepresentative]);

  const loadRepresentatives = async () => {
    try {
      const users = await getUsers();
      setAllUsers(users);
      const reps = users.filter(u => u.role === 'representative' || u.role === 'temsilci');
      setRepresentatives(reps);
    } catch (error) {
      console.error('Temsilciler yüklenemedi:', error);
    }
  };

  const loadTargets = async () => {
    try {
      const targetsData = await getTargets();
      setTargets(targetsData);
    } catch (error) {
      console.error('Hedefler yüklenemedi:', error);
    }
  };

  const loadAllData = async () => {
    try {
      setLoading(true);
      const repFilter = selectedRepresentative === 'all' ? null : selectedRepresentative;
      
      const [dashboard, salesQuantity, productProfit, representativeRevenue] = await Promise.all([
        getDashboardReport(repFilter, startDate, endDate),
        getSalesQuantityReport(startDate, endDate, selectedRepresentative),
        getProductCompanyProfitReport(startDate, endDate, selectedRepresentative),
        getRepresentativeRevenueReport(startDate, endDate)
      ]);
      
      setDashboardData(dashboard);
      setSalesQuantityData(salesQuantity);
      setProductProfitData(productProfit);
      
      if (selectedRepresentative === 'all') {
        setRepresentativeRevenueData(representativeRevenue);
      } else {
        setRepresentativeRevenueData(representativeRevenue.filter(r => r.representative === selectedRepresentative));
      }
      
      // Yeni/Mevcut müşteri satış verilerini hesapla
      await loadCustomerSalesData();
      
      // Performans verisi
      if (selectedRepresentative !== 'all') {
        await loadPerformanceData();
      } else {
        setPerformanceData(null);
      }
    } catch (error) {
      toast.error('Veriler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  // Yeni/Mevcut müşteri satış verilerini yükle
  const loadCustomerSalesData = async () => {
    try {
      const token = localStorage.getItem('token');
      const repParam = selectedRepresentative !== 'all' ? `&representative=${selectedRepresentative}` : '';
      
      // Satışları çek (tüm satışlar - filtresiz)
      const allSalesResponse = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/sales?token=${token}`
      );
      
      // Müşterileri çek
      const customersResponse = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/customers?token=${token}`
      );
      
      if (allSalesResponse.ok && customersResponse.ok) {
        let allSales = await allSalesResponse.json();
        const customers = await customersResponse.json();
        
        // Müşteri oluşturma tarihlerini map'le
        // Eğer created_at yoksa, o müşterinin ilk satış tarihini kullan
        const customerCreatedDates = {};
        
        // Önce müşteri kayıtlarından created_at'ı al
        customers.forEach(c => {
          if (c.created_at) {
            customerCreatedDates[c.name] = c.created_at.substring(0, 10);
          }
        });
        
        // created_at olmayan müşteriler için ilk satış tarihini bul
        const customerFirstSale = {};
        allSales.forEach(sale => {
          const customerName = sale.customer_name;
          const saleDate = sale.sale_date?.substring(0, 10);
          if (saleDate) {
            if (!customerFirstSale[customerName] || saleDate < customerFirstSale[customerName]) {
              customerFirstSale[customerName] = saleDate;
            }
          }
        });
        
        // created_at olmayanlar için ilk satış tarihini ata
        Object.keys(customerFirstSale).forEach(name => {
          if (!customerCreatedDates[name]) {
            customerCreatedDates[name] = customerFirstSale[name];
          }
        });
        
        // Tarih filtreleme - seçilen döneme göre
        let sales = allSales.filter(sale => {
          const saleDate = sale.sale_date?.substring(0, 10);
          return saleDate >= startDate && saleDate <= endDate;
        });
        
        // Temsilci filtreleme
        if (selectedRepresentative !== 'all') {
          sales = sales.filter(sale => sale.representative === selectedRepresentative);
        }
        
        // Yeni vs Mevcut müşteri satışlarını ayır
        const newCustomerSales = [];
        const existingCustomerSales = [];
        const newCustomerNames = new Set();
        const existingCustomerNames = new Set();
        
        sales.forEach(sale => {
          const customerName = sale.customer_name;
          const customerCreatedDate = customerCreatedDates[customerName] || '2020-01-01';
          
          // Müşteri bu dönemde oluşturulduysa "yeni müşteri"
          const isNewCustomer = customerCreatedDate >= startDate && customerCreatedDate <= endDate;
          
          if (isNewCustomer) {
            newCustomerSales.push(sale);
            newCustomerNames.add(customerName);
          } else {
            existingCustomerSales.push(sale);
            existingCustomerNames.add(customerName);
          }
        });
        
        // Ziyaret verilerini çek
        const visitsResponse = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/visits?token=${token}${repParam}`
        );
        let newCustomerVisits = 0;
        let existingCustomerVisits = 0;
        let newVisitCustomers = new Set();
        let existingVisitCustomers = new Set();
        
        if (visitsResponse.ok) {
          let visits = await visitsResponse.json();
          visits = visits.filter(visit => {
            const visitDate = visit.visit_date?.substring(0, 10);
            return visitDate >= startDate && visitDate <= endDate;
          });
          
          // Temsilci filtreleme
          if (selectedRepresentative !== 'all') {
            visits = visits.filter(v => v.representative === selectedRepresentative);
          }
          
          visits.forEach(visit => {
            const customerName = visit.customer_name;
            const customerCreatedDate = customerCreatedDates[customerName] || '2020-01-01';
            const isNewCustomer = customerCreatedDate >= startDate && customerCreatedDate <= endDate;
            
            if (isNewCustomer) {
              newCustomerVisits++;
              newVisitCustomers.add(customerName);
            } else {
              existingCustomerVisits++;
              existingVisitCustomers.add(customerName);
            }
          });
        }
        
        // Arama verilerini çek
        const callsResponse = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/calls?token=${token}${repParam}`
        );
        let newCustomerCalls = 0;
        let existingCustomerCalls = 0;
        let newCallCustomers = new Set();
        let existingCallCustomers = new Set();
        
        if (callsResponse.ok) {
          let calls = await callsResponse.json();
          calls = calls.filter(call => {
            const callDate = call.call_date?.substring(0, 10);
            return callDate >= startDate && callDate <= endDate;
          });
          
          // Temsilci filtreleme
          if (selectedRepresentative !== 'all') {
            calls = calls.filter(c => c.representative === selectedRepresentative);
          }
          
          calls.forEach(call => {
            const customerName = call.customer_name;
            const customerCreatedDate = customerCreatedDates[customerName] || '2020-01-01';
            const isNewCustomer = customerCreatedDate >= startDate && customerCreatedDate <= endDate;
            
            if (isNewCustomer) {
              newCustomerCalls++;
              newCallCustomers.add(customerName);
            } else {
              existingCustomerCalls++;
              existingCallCustomers.add(customerName);
            }
          });
        }
        
        setCustomerSalesData({
          newCustomers: newCustomerNames.size,
          existingCustomers: existingCustomerNames.size,
          newCustomerSales,
          existingCustomerSales,
          newCustomerRevenue: newCustomerSales.reduce((sum, s) => sum + (s.price || 0), 0),
          existingCustomerRevenue: existingCustomerSales.reduce((sum, s) => sum + (s.price || 0), 0),
          newCustomerVisits,
          existingCustomerVisits,
          newVisitCustomers: newVisitCustomers.size,
          existingVisitCustomers: existingVisitCustomers.size,
          newCustomerCalls,
          existingCustomerCalls,
          newCallCustomers: newCallCustomers.size,
          existingCallCustomers: existingCallCustomers.size
        });
      }
    } catch (error) {
      console.error('Müşteri satış verileri yüklenemedi:', error);
    }
  };

  const loadPerformanceData = async () => {
    if (selectedRepresentative === 'all') return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/reports/representative-performance?token=${token}&representative=${selectedRepresentative}&start_date=${startDate}&end_date=${endDate}`
      );
      
      if (response.ok) {
        const data = await response.json();
        setPerformanceData(data);
      }
    } catch (error) {
      console.error('Performans verisi yüklenemedi:', error);
    }
  };

  // Detaylı satış raporu yükleme
  const loadDetailedSalesReport = async () => {
    setDetailedSalesLoading(true);
    setShowDetailedSalesModal(true);
    
    try {
      const token = localStorage.getItem('token');
      const repParam = selectedRepresentative !== 'all' ? `&representative=${selectedRepresentative}` : '';
      
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/sales?token=${token}${repParam}`
      );
      
      if (response.ok) {
        let sales = await response.json();
        
        // Tarih filtreleme
        sales = sales.filter(sale => {
          const saleDate = sale.sale_date?.substring(0, 10);
          return saleDate >= startDate && saleDate <= endDate;
        });
        
        // Detaylı veri hazırlama
        const detailedData = [];
        sales.forEach(sale => {
          if (sale.items && sale.items.length > 0) {
            sale.items.forEach(item => {
              detailedData.push({
                date: sale.sale_date,
                customer: sale.customer_name,
                representative: sale.representative,
                category: item.category || 'Diğer',
                product: item.product_name,
                quantity: item.quantity,
                unit_price: item.unit_price || item.discounted_price,
                total: item.total
              });
            });
          } else if (sale.product_name) {
            detailedData.push({
              date: sale.sale_date,
              customer: sale.customer_name,
              representative: sale.representative,
              category: sale.category || 'Diğer',
              product: sale.product_name,
              quantity: sale.quantity,
              unit_price: sale.price / (sale.quantity || 1),
              total: sale.price
            });
          }
        });
        
        // Tarihe göre sırala (en yeni üstte)
        detailedData.sort((a, b) => new Date(b.date) - new Date(a.date));
        setDetailedSalesData(detailedData);
      }
    } catch (error) {
      toast.error('Detaylı satış raporu yüklenemedi');
    } finally {
      setDetailedSalesLoading(false);
    }
  };

  const handleRepPerformanceClick = async (rep, type) => {
    try {
      const details = await getRepDetails(rep, type, startDate, endDate);
      setRepDetailsData({ type, rep, data: details });
      setShowRepDetailsDialog(true);
    } catch (error) {
      toast.error('Detaylar yüklenirken hata oluştu');
    }
  };

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  // Hedef gerçekleştirme hesaplama
  const getTargetAchievement = (username) => {
    const now = new Date();
    const currentMonth = now.getMonth() + 1;
    const currentYear = now.getFullYear();
    
    const target = targets.find(t => 
      t.username === username && 
      t.month === currentMonth && 
      t.year === currentYear
    );
    
    if (!target) return null;
    
    return {
      targetAmount: target.target_amount,
      realizedAmount: target.realized_amount || 0,
      rate: target.realization_rate || 0
    };
  };

  // Excel export
  const exportToExcel = () => {
    if (!dashboardData) {
      toast.error('Export edilecek veri bulunamadı');
      return;
    }

    const allReps = new Set([
      ...Object.keys(dashboardData.rep_visits_tm || {}),
      ...Object.keys(dashboardData.rep_sales_tm || {})
    ]);

    let csv = 'Temsilci,Ziyaret,Arama,Arvento,Kamera,Sensör,M2M,Hedef,Gerçekleşen,Oran (%)\n';
    
    allReps.forEach(rep => {
      const visits = dashboardData.rep_visits_tm?.[rep] || 0;
      const calls = dashboardData.rep_calls_tm?.[rep] || 0;
      const arvento = dashboardData.rep_sales_tm?.[rep] || 0;
      const camera = dashboardData.rep_cameras_tm?.[rep] || 0;
      const sensor = dashboardData.rep_sensors_tm?.[rep] || 0;
      const m2m = dashboardData.rep_m2m_tm?.[rep] || 0;
      
      const achievement = getTargetAchievement(rep);
      const targetAmount = achievement?.targetAmount || 0;
      const realizedAmount = achievement?.realizedAmount || 0;
      const rate = achievement?.rate || 0;
      
      csv += `${rep},${visits},${calls},${arvento},${camera},${sensor},${m2m},${targetAmount},${realizedAmount},${rate.toFixed(1)}\n`;
    });

    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `temsilci_performans_${startDate}_${endDate}.csv`;
    link.click();
    
    toast.success('Rapor başarıyla indirildi');
  };

  // Grafik renkleri
  const COLORS = ['#3b82f6', '#10b981', '#8b5cf6', '#f59e0b', '#ef4444', '#06b6d4', '#ec4899', '#14b8a6'];

  // Toplam hesaplamalar
  const totalCompanyProfit = productProfitData.reduce((sum, item) => sum + (item.company_profit || 0), 0);
  const totalRevenue = representativeRevenueData.reduce((sum, item) => sum + (item.revenue || 0), 0);
  const totalProfit = representativeRevenueData.reduce((sum, item) => sum + (item.profit || 0), 0);

  // Grafik verileri
  const productChartData = productProfitData.slice(0, 10).map(item => ({
    name: item.product_name?.substring(0, 20) || 'Diğer',
    'Şirket Karı': item.company_profit || 0
  }));

  const representativeChartData = representativeRevenueData.map(item => ({
    name: item.representative,
    'Ciro': item.revenue || 0,
    'Puan': item.profit || 0
  }));

  // Dashboard tablosu için repData
  const repData = dashboardData ? {
    visits: dashboardData.rep_visits_tm || {},
    calls: dashboardData.rep_calls_tm || {},
    sales: dashboardData.rep_sales_tm || {},
    cameras: dashboardData.rep_cameras_tm || {},
    sensors: dashboardData.rep_sensors_tm || {},
    m2m: dashboardData.rep_m2m_tm || {}
  } : { visits: {}, calls: {}, sales: {}, cameras: {}, sensors: {}, m2m: {} };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        icon={TrendingUp}
        title="Temsilci Performans Raporu"
        subtitle="Detaylı satış ve hedef gerçekleştirme analizi"
      />

      {/* FİLTRELER - YENİ TASARIM */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 md:p-6">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-5 h-5 text-gray-500" />
          <h3 className="font-semibold text-gray-700">Filtreler</h3>
        </div>
        
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Tarih Filtreleri */}
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={() => setDateFilter('thisMonth')}
              variant={dateFilter === 'thisMonth' ? 'default' : 'outline'}
              size="sm"
              className={dateFilter === 'thisMonth' ? 'bg-green-600 hover:bg-green-700' : ''}
              data-testid="filter-this-month"
            >
              <Calendar className="w-4 h-4 mr-1" />
              Bu Ay
            </Button>
            <Button
              onClick={() => setDateFilter('lastMonth')}
              variant={dateFilter === 'lastMonth' ? 'default' : 'outline'}
              size="sm"
              className={dateFilter === 'lastMonth' ? 'bg-blue-600 hover:bg-blue-700' : ''}
              data-testid="filter-last-month"
            >
              <Calendar className="w-4 h-4 mr-1" />
              Geçen Ay
            </Button>
            <Button
              onClick={() => setDateFilter('allTime')}
              variant={dateFilter === 'allTime' ? 'default' : 'outline'}
              size="sm"
              className={dateFilter === 'allTime' ? 'bg-purple-600 hover:bg-purple-700' : ''}
              data-testid="filter-all-time"
            >
              <Clock className="w-4 h-4 mr-1" />
              Tüm Zamanlar
            </Button>
            <Button
              onClick={() => setDateFilter('custom')}
              variant={dateFilter === 'custom' ? 'default' : 'outline'}
              size="sm"
              className={dateFilter === 'custom' ? 'bg-orange-600 hover:bg-orange-700' : ''}
              data-testid="filter-custom"
            >
              <Calendar className="w-4 h-4 mr-1" />
              Detaylı
            </Button>
          </div>

          {/* Detaylı Tarih Seçici */}
          {dateFilter === 'custom' && (
            <div className="flex items-center gap-2 flex-wrap">
              <div className="relative">
                <label className="absolute -top-2 left-2 bg-white px-1 text-xs text-gray-600 z-10">Başlangıç</label>
                <input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  data-testid="custom-start-date"
                />
              </div>
              <span className="text-gray-400">—</span>
              <div className="relative">
                <label className="absolute -top-2 left-2 bg-white px-1 text-xs text-gray-600 z-10">Bitiş</label>
                <input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  data-testid="custom-end-date"
                />
              </div>
            </div>
          )}

          {/* Temsilci Filtresi - Admin için */}
          {user?.role !== 'temsilci' && (
            <div className="relative ml-auto">
              <label className="absolute -top-2 left-2 bg-white px-1 text-xs text-gray-600 z-10">Temsilci</label>
              <select
                value={selectedRepresentative}
                onChange={(e) => setSelectedRepresentative(e.target.value)}
                className="border border-gray-300 rounded-lg px-4 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-w-[200px] appearance-none bg-white"
                data-testid="representative-filter"
              >
                <option value="all">Tüm Temsilciler</option>
                {representatives.map(rep => (
                  <option key={rep.id} value={rep.username}>{rep.full_name || rep.username}</option>
                ))}
              </select>
              <ChevronDown className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            </div>
          )}
        </div>
        
        {/* Aktif Filtre Bilgisi */}
        <div className="mt-4 flex items-center gap-2 text-sm text-gray-600">
          <span className="bg-gray-100 px-3 py-1 rounded-full">
            📅 {format(new Date(startDate), 'd MMM yyyy', { locale: tr })} - {format(new Date(endDate), 'd MMM yyyy', { locale: tr })}
          </span>
          {selectedRepresentative !== 'all' && (
            <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full">
              👤 {selectedRepresentative}
            </span>
          )}
        </div>
      </div>

      {/* SATIŞ ADET KATEGORİ KARTLARI - SAYFA ÜSTÜNDE */}
      {salesQuantityData && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
              <Package className="w-5 h-5 text-slate-600" />
              Satış Adet Özeti
            </h3>
            <div className="flex gap-2">
              <Button
                onClick={loadDetailedSalesReport}
                className="bg-slate-700 hover:bg-slate-800 text-white flex items-center gap-2"
                size="sm"
                data-testid="view-detailed-sales-btn"
              >
                <BarChart3 className="w-4 h-4" />
                Tüm Detayları Gör
              </Button>
              <Button
                onClick={() => navigate('/sales-quantity-reports')}
                variant="outline"
                size="sm"
                className="text-slate-700 border-slate-300 hover:bg-slate-50"
              >
                Adet Raporları
              </Button>
            </div>
          </div>
          
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-3">
            {/* Arvento Peşin */}
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
              <h4 className="text-xs font-medium text-gray-500 mb-1">Arvento Peşin</h4>
              <p className="text-2xl font-bold text-slate-800">{salesQuantityData.category_totals?.['Arvento Peşin'] || 0}</p>
            </div>
            
            {/* Arvento Operatör */}
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
              <h4 className="text-xs font-medium text-gray-500 mb-1">Arvento Operatör</h4>
              <p className="text-2xl font-bold text-slate-800">{salesQuantityData.category_totals?.['Arvento Operatör'] || 0}</p>
            </div>
            
            {/* Arvento Taksitli */}
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
              <h4 className="text-xs font-medium text-gray-500 mb-1">Arvento Taksitli</h4>
              <p className="text-2xl font-bold text-slate-800">{salesQuantityData.category_totals?.['Arvento Taksitli'] || 0}</p>
            </div>
            
            {/* M2M */}
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
              <h4 className="text-xs font-medium text-gray-500 mb-1">M2M</h4>
              <p className="text-2xl font-bold text-slate-800">{salesQuantityData.category_totals?.['M2M'] || 0}</p>
            </div>
            
            {/* Sensörler */}
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
              <h4 className="text-xs font-medium text-gray-500 mb-1">Sensörler</h4>
              <p className="text-2xl font-bold text-slate-800">{salesQuantityData.category_totals?.['Sensörler'] || 0}</p>
            </div>
            
            {/* Kamera Ürünleri */}
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
              <h4 className="text-xs font-medium text-gray-500 mb-1">Kamera Ürünleri</h4>
              <p className="text-2xl font-bold text-slate-800">{salesQuantityData.category_totals?.['Kamera Ürünleri'] || 0}</p>
            </div>
            
            {/* Diğer */}
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
              <h4 className="text-xs font-medium text-gray-500 mb-1">Diğer</h4>
              <p className="text-2xl font-bold text-slate-800">{salesQuantityData.category_totals?.['Diğer'] || 0}</p>
            </div>
          </div>
          
          {/* Genel Toplam */}
          <div className="bg-slate-800 rounded-xl p-4 text-white shadow-md flex items-center justify-between">
            <div className="flex items-center gap-3">
              <ShoppingCart className="w-8 h-8 opacity-80" />
              <div>
                <h4 className="text-sm font-medium opacity-90">Genel Toplam</h4>
                <p className="text-xs opacity-75">Tüm kategoriler</p>
              </div>
            </div>
            <p className="text-3xl font-bold">
              {Object.values(salesQuantityData.category_totals || {}).reduce((a, b) => a + b, 0)}
            </p>
          </div>
        </div>
      )}

      {/* HEDEF GERÇEKLEŞTİRME ÖZET KARTLARI */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="p-2 bg-slate-100 rounded-lg">
              <DollarSign className="w-6 h-6 text-slate-600" />
            </div>
            <span className="text-2xl font-bold text-slate-800">₺{totalRevenue.toLocaleString('tr-TR')}</span>
          </div>
          <h3 className="text-sm font-medium text-gray-500">Toplam Ciro</h3>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="p-2 bg-slate-100 rounded-lg">
              <Award className="w-6 h-6 text-slate-600" />
            </div>
            <span className="text-2xl font-bold text-slate-800">₺{totalProfit.toLocaleString('tr-TR')}</span>
          </div>
          <h3 className="text-sm font-medium text-gray-500">Toplam Puan</h3>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="p-2 bg-slate-100 rounded-lg">
              <TrendingUp className="w-6 h-6 text-slate-600" />
            </div>
            <span className="text-2xl font-bold text-slate-800">₺{totalCompanyProfit.toLocaleString('tr-TR')}</span>
          </div>
          <h3 className="text-sm font-medium text-gray-500">Şirket Karı</h3>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="p-2 bg-slate-100 rounded-lg">
              <Package className="w-6 h-6 text-slate-600" />
            </div>
            <span className="text-2xl font-bold text-slate-800">
              {salesQuantityData ? Object.values(salesQuantityData.category_totals || {}).reduce((a, b) => a + b, 0) : 0}
            </span>
          </div>
          <h3 className="text-sm font-medium text-gray-500">Toplam Satış Adedi</h3>
        </div>
      </div>

      {/* YENİ / MEVCUT MÜŞTERİ SATIŞ KARŞILAŞTIRMASI */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
          <ShoppingCart className="w-5 h-5 text-slate-600" />
          Satış Analizi (Yeni / Mevcut Müşteri)
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-slate-100 rounded-xl">
                  <UserPlus className="w-6 h-6 text-slate-600" />
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-700">Yeni Müşteri Satışları</h3>
                  <p className="text-xs text-gray-400">Bu dönemde oluşturulan müşteriler</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-slate-800">{customerSalesData.newCustomers}</p>
                <p className="text-xs text-gray-500">müşteri</p>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-gray-100">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Toplam Ciro</span>
                <span className="text-lg font-bold text-slate-800">
                  ₺{(customerSalesData.newCustomerRevenue || 0).toLocaleString('tr-TR')}
                </span>
              </div>
              <div className="flex justify-between items-center mt-1">
                <span className="text-sm text-gray-600">Satış Sayısı</span>
                <span className="text-sm font-semibold text-gray-700">
                  {customerSalesData.newCustomerSales?.length || 0} satış
                </span>
              </div>
              <div className="flex justify-between items-center mt-2 pt-2 border-t border-gray-100">
                <span className="text-sm text-gray-600">Ciro Oranı</span>
                <span className="text-sm font-bold text-emerald-600">
                  %{((customerSalesData.newCustomerRevenue || 0) / ((customerSalesData.newCustomerRevenue || 0) + (customerSalesData.existingCustomerRevenue || 0)) * 100 || 0).toFixed(1)}
                </span>
              </div>
              <div className="flex justify-between items-center mt-1">
                <span className="text-sm text-gray-600">Müşteri Oranı</span>
                <span className="text-sm font-bold text-emerald-600">
                  %{((customerSalesData.newCustomers || 0) / ((customerSalesData.newCustomers || 0) + (customerSalesData.existingCustomers || 0)) * 100 || 0).toFixed(1)}
                </span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-slate-100 rounded-xl">
                  <UserCheck className="w-6 h-6 text-slate-600" />
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-700">Mevcut Müşteri Satışları</h3>
                  <p className="text-xs text-gray-400">Daha önce kayıtlı müşteriler</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-slate-800">{customerSalesData.existingCustomers}</p>
                <p className="text-xs text-gray-500">müşteri</p>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-gray-100">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Toplam Ciro</span>
                <span className="text-lg font-bold text-slate-800">
                  ₺{(customerSalesData.existingCustomerRevenue || 0).toLocaleString('tr-TR')}
                </span>
              </div>
              <div className="flex justify-between items-center mt-1">
                <span className="text-sm text-gray-600">Satış Sayısı</span>
                <span className="text-sm font-semibold text-gray-700">
                  {customerSalesData.existingCustomerSales?.length || 0} satış
                </span>
              </div>
              <div className="flex justify-between items-center mt-2 pt-2 border-t border-gray-100">
                <span className="text-sm text-gray-600">Ciro Oranı</span>
                <span className="text-sm font-bold text-slate-600">
                  %{((customerSalesData.existingCustomerRevenue || 0) / ((customerSalesData.newCustomerRevenue || 0) + (customerSalesData.existingCustomerRevenue || 0)) * 100 || 0).toFixed(1)}
                </span>
              </div>
              <div className="flex justify-between items-center mt-1">
                <span className="text-sm text-gray-600">Müşteri Oranı</span>
                <span className="text-sm font-bold text-slate-600">
                  %{((customerSalesData.existingCustomers || 0) / ((customerSalesData.newCustomers || 0) + (customerSalesData.existingCustomers || 0)) * 100 || 0).toFixed(1)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* TEMSİLCİ ZİYARET ANALİZİ (Yeni / Mevcut Müşteri) */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
          <MapPin className="w-5 h-5 text-slate-600" />
          Ziyaret Analizi (Yeni / Mevcut Müşteri)
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-slate-100 rounded-xl">
                  <UserPlus className="w-6 h-6 text-slate-600" />
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-700">Yeni Müşteri Ziyaretleri</h3>
                  <p className="text-xs text-gray-400">Bu dönemde oluşturulan müşterilere</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-slate-800">{customerSalesData.newCustomerVisits || 0}</p>
                <p className="text-xs text-gray-500">ziyaret</p>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-gray-100">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Müşteri Sayısı</span>
                <span className="text-sm font-semibold text-gray-700">
                  {customerSalesData.newVisitCustomers || 0} müşteri
                </span>
              </div>
              <div className="flex justify-between items-center mt-2 pt-2 border-t border-gray-100">
                <span className="text-sm text-gray-600">Ziyaret Oranı</span>
                <span className="text-sm font-bold text-emerald-600">
                  %{((customerSalesData.newCustomerVisits || 0) / ((customerSalesData.newCustomerVisits || 0) + (customerSalesData.existingCustomerVisits || 0)) * 100 || 0).toFixed(1)}
                </span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-slate-100 rounded-xl">
                  <UserCheck className="w-6 h-6 text-slate-600" />
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-700">Mevcut Müşteri Ziyaretleri</h3>
                  <p className="text-xs text-gray-400">Daha önce kayıtlı müşterilere</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-slate-800">{customerSalesData.existingCustomerVisits || 0}</p>
                <p className="text-xs text-gray-500">ziyaret</p>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-gray-100">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Müşteri Sayısı</span>
                <span className="text-sm font-semibold text-gray-700">
                  {customerSalesData.existingVisitCustomers || 0} müşteri
                </span>
              </div>
              <div className="flex justify-between items-center mt-2 pt-2 border-t border-gray-100">
                <span className="text-sm text-gray-600">Ziyaret Oranı</span>
                <span className="text-sm font-bold text-slate-600">
                  %{((customerSalesData.existingCustomerVisits || 0) / ((customerSalesData.newCustomerVisits || 0) + (customerSalesData.existingCustomerVisits || 0)) * 100 || 0).toFixed(1)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ABONELİK ARAMA ANALİZİ (Yeni / Mevcut Müşteri) */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
          <Phone className="w-5 h-5 text-slate-600" />
          Arama Analizi (Yeni / Mevcut Müşteri)
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-slate-100 rounded-xl">
                  <UserPlus className="w-6 h-6 text-slate-600" />
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-700">Yeni Müşteri Aramaları</h3>
                  <p className="text-xs text-gray-400">Bu dönemde oluşturulan müşterilere</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-slate-800">{customerSalesData.newCustomerCalls || 0}</p>
                <p className="text-xs text-gray-500">arama</p>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-gray-100">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Müşteri Sayısı</span>
                <span className="text-sm font-semibold text-gray-700">
                  {customerSalesData.newCallCustomers || 0} müşteri
                </span>
              </div>
              <div className="flex justify-between items-center mt-2 pt-2 border-t border-gray-100">
                <span className="text-sm text-gray-600">Arama Oranı</span>
                <span className="text-sm font-bold text-emerald-600">
                  %{((customerSalesData.newCustomerCalls || 0) / ((customerSalesData.newCustomerCalls || 0) + (customerSalesData.existingCustomerCalls || 0)) * 100 || 0).toFixed(1)}
                </span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-slate-100 rounded-xl">
                  <UserCheck className="w-6 h-6 text-slate-600" />
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-700">Mevcut Müşteri Aramaları</h3>
                  <p className="text-xs text-gray-400">Daha önce kayıtlı müşterilere</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-slate-800">{customerSalesData.existingCustomerCalls || 0}</p>
                <p className="text-xs text-gray-500">arama</p>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-gray-100">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Müşteri Sayısı</span>
                <span className="text-sm font-semibold text-gray-700">
                  {customerSalesData.existingCallCustomers || 0} müşteri
                </span>
              </div>
              <div className="flex justify-between items-center mt-2 pt-2 border-t border-gray-100">
                <span className="text-sm text-gray-600">Arama Oranı</span>
                <span className="text-sm font-bold text-slate-600">
                  %{((customerSalesData.existingCustomerCalls || 0) / ((customerSalesData.newCustomerCalls || 0) + (customerSalesData.existingCustomerCalls || 0)) * 100 || 0).toFixed(1)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* HEDEF GERÇEKLEŞTİRME TABLOSU */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-4 md:p-6 border-b bg-gray-50 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <div>
            <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
              <Target className="w-5 h-5 text-slate-600" />
              Hedef Gerçekleştirme Oranları
            </h3>
            <p className="text-sm text-gray-500">Aylık hedef ve gerçekleşme durumu</p>
          </div>
          {user?.role === 'admin' && (
            <Button 
              onClick={exportToExcel}
              className="bg-slate-700 hover:bg-slate-800 text-white flex items-center gap-2"
              data-testid="export-btn"
            >
              <Download className="w-4 h-4" />
              Excel'e Aktar
            </Button>
          )}
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="target-achievement-table">
            <thead className="bg-slate-700 text-white">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold">Temsilci</th>
                <th className="px-4 py-3 text-center text-sm font-semibold">Ziyaret</th>
                <th className="px-4 py-3 text-center text-sm font-semibold">Arama</th>
                <th className="px-4 py-3 text-center text-sm font-semibold">Arvento</th>
                <th className="px-4 py-3 text-center text-sm font-semibold">Kamera</th>
                <th className="px-4 py-3 text-center text-sm font-semibold">Sensör</th>
                <th className="px-4 py-3 text-center text-sm font-semibold">M2M</th>
                <th className="px-4 py-3 text-center text-sm font-semibold">Hedef</th>
                <th className="px-4 py-3 text-center text-sm font-semibold">Gerçekleşen</th>
                <th className="px-4 py-3 text-center text-sm font-semibold">Oran</th>
              </tr>
            </thead>
            <tbody>
              {(() => {
                // Filtrelenmiş kullanıcılar
                let filteredUsers = allUsers.filter(u => u.role === 'temsilci' || u.role === 'abonelik');
                
                // Eğer temsilci rolündeyse sadece kendini göster
                if (user?.role === 'temsilci') {
                  filteredUsers = filteredUsers.filter(u => u.username === user.username);
                }
                // Eğer belirli bir temsilci seçildiyse
                else if (selectedRepresentative !== 'all') {
                  filteredUsers = filteredUsers.filter(u => u.username === selectedRepresentative);
                }
                
                if (filteredUsers.length === 0) {
                  return (
                    <tr>
                      <td colSpan="10" className="px-6 py-12 text-center text-gray-500">
                        Henüz veri yok
                      </td>
                    </tr>
                  );
                }
                
                // Hedef oranına göre sırala
                const repsWithAchievement = filteredUsers.map(u => {
                  const achievement = getTargetAchievement(u.username);
                  return { 
                    user: u, 
                    achievement,
                    rate: achievement?.rate ?? -1
                  };
                });
                
                const sortedReps = repsWithAchievement.sort((a, b) => b.rate - a.rate);
                
                return sortedReps.map(({ user: u, achievement }, index) => {
                  const rep = u.username;
                  
                  const getRowBgColor = () => {
                    if (!achievement) return 'hover:bg-gray-50';
                    const rate = achievement.rate;
                    if (rate >= 100) return 'bg-gray-50 hover:bg-gray-100';
                    return 'hover:bg-gray-50';
                  };
                  
                  const getAchievementBadge = () => {
                    if (!achievement) return <span className="text-gray-400 text-xs">Hedef yok</span>;
                    const rate = achievement.rate;
                    
                    if (rate >= 100) {
                      return (
                        <div className="flex items-center gap-1">
                          <CheckCircle className="w-4 h-4 text-emerald-600" />
                          <span className="text-emerald-700 font-bold">{rate.toFixed(1)}%</span>
                        </div>
                      );
                    }
                    return (
                      <div className="flex items-center gap-1">
                        <AlertCircle className="w-4 h-4 text-slate-400" />
                        <span className="text-slate-600 font-bold">{rate.toFixed(1)}%</span>
                      </div>
                    );
                  };
                  
                  return (
                    <tr key={rep} className={`border-b transition-colors ${getRowBgColor()}`}>
                      <td className="px-4 py-3">
                        <div>
                          <p className="font-medium text-gray-900">{u.full_name || rep}</p>
                          <p className="text-xs text-gray-500">@{rep}</p>
                        </div>
                      </td>
                      <td 
                        className="px-4 py-3 text-center cursor-pointer hover:bg-slate-100 transition-colors"
                        onClick={() => handleRepPerformanceClick(rep, 'visits')}
                      >
                        <span className="bg-slate-100 text-slate-700 px-2.5 py-1 rounded-full text-xs font-semibold">
                          {repData.visits[rep] || 0}
                        </span>
                      </td>
                      <td 
                        className="px-4 py-3 text-center cursor-pointer hover:bg-slate-100 transition-colors"
                        onClick={() => handleRepPerformanceClick(rep, 'calls')}
                      >
                        <span className="bg-slate-100 text-slate-700 px-2.5 py-1 rounded-full text-xs font-semibold">
                          {repData.calls?.[rep] || 0}
                        </span>
                      </td>
                      <td 
                        className="px-4 py-3 text-center cursor-pointer hover:bg-slate-100 transition-colors"
                        onClick={() => handleRepPerformanceClick(rep, 'arvento')}
                      >
                        <span className="bg-slate-100 text-slate-700 px-2.5 py-1 rounded-full text-xs font-semibold">
                          {repData.sales[rep] || 0}
                        </span>
                      </td>
                      <td 
                        className="px-4 py-3 text-center cursor-pointer hover:bg-slate-100 transition-colors"
                        onClick={() => handleRepPerformanceClick(rep, 'camera')}
                      >
                        <span className="bg-slate-100 text-slate-700 px-2.5 py-1 rounded-full text-xs font-semibold">
                          {repData.cameras[rep] || 0}
                        </span>
                      </td>
                      <td 
                        className="px-4 py-3 text-center cursor-pointer hover:bg-slate-100 transition-colors"
                        onClick={() => handleRepPerformanceClick(rep, 'sensors')}
                      >
                        <span className="bg-slate-100 text-slate-700 px-2.5 py-1 rounded-full text-xs font-semibold">
                          {repData.sensors?.[rep] || 0}
                        </span>
                      </td>
                      <td 
                        className="px-4 py-3 text-center cursor-pointer hover:bg-slate-100 transition-colors"
                        onClick={() => handleRepPerformanceClick(rep, 'm2m')}
                      >
                        <span className="bg-slate-100 text-slate-700 px-2.5 py-1 rounded-full text-xs font-semibold">
                          {repData.m2m?.[rep] || 0}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="text-gray-700 font-medium">
                          {achievement ? `₺${achievement.targetAmount.toLocaleString('tr-TR')}` : '-'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="text-gray-700 font-medium">
                          {achievement ? `₺${achievement.realizedAmount.toLocaleString('tr-TR')}` : '-'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        {getAchievementBadge()}
                      </td>
                    </tr>
                  );
                });
              })()}
            </tbody>
          </table>
        </div>
      </div>

      {/* TEMSİLCİ PERFORMANS ÖZETİ - Tek temsilci seçildiğinde */}
      {selectedRepresentative !== 'all' && performanceData && (
        <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl shadow-xl p-6 text-white">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold">{performanceData.representative}</h2>
              <p className="text-slate-400 text-sm">
                {format(new Date(startDate), 'd MMM yyyy', { locale: tr })} - {format(new Date(endDate), 'd MMM yyyy', { locale: tr })}
              </p>
            </div>
            <div className="p-3 bg-white bg-opacity-10 rounded-xl">
              <Users className="w-8 h-8" />
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
            <div className="bg-white bg-opacity-10 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <MapPin className="w-4 h-4 text-blue-400" />
                <span className="text-xs text-slate-300">Ziyaret</span>
              </div>
              <p className="text-2xl font-bold">{performanceData.metrics.visit_count}</p>
            </div>
            
            <div className="bg-white bg-opacity-10 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <Phone className="w-4 h-4 text-green-400" />
                <span className="text-xs text-slate-300">Arama</span>
              </div>
              <p className="text-2xl font-bold">{performanceData.metrics.call_count}</p>
            </div>
            
            <div className="bg-white bg-opacity-10 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <ShoppingCart className="w-4 h-4 text-purple-400" />
                <span className="text-xs text-slate-300">Satış</span>
              </div>
              <p className="text-2xl font-bold">{performanceData.metrics.sales_count}</p>
            </div>
            
            <div className="bg-white bg-opacity-10 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <DollarSign className="w-4 h-4 text-yellow-400" />
                <span className="text-xs text-slate-300">Ciro</span>
              </div>
              <p className="text-xl font-bold">₺{performanceData.metrics.total_revenue.toLocaleString('tr-TR')}</p>
            </div>
            
            <div className="bg-white bg-opacity-10 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="w-4 h-4 text-emerald-400" />
                <span className="text-xs text-slate-300">Şirket Karı</span>
              </div>
              <p className="text-xl font-bold">₺{performanceData.metrics.total_company_profit.toLocaleString('tr-TR')}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white bg-opacity-5 rounded-xl p-4">
              <h3 className="text-sm font-semibold mb-4 flex items-center gap-2">
                <BarChart3 className="w-4 h-4" />
                Aylık Performans
              </h3>
              <ResponsiveContainer width="100%" height={200}>
                <ComposedChart data={performanceData.monthly_performance}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis dataKey="name" tick={{ fill: '#9ca3af', fontSize: 11 }} />
                  <YAxis yAxisId="left" tick={{ fill: '#9ca3af', fontSize: 11 }} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fill: '#9ca3af', fontSize: 11 }} />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#1f2937', border: 'none', borderRadius: '8px' }}
                    labelStyle={{ color: '#fff' }}
                  />
                  <Legend wrapperStyle={{ fontSize: '11px' }} />
                  <Bar yAxisId="left" dataKey="sales" name="Satış" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                  <Bar yAxisId="left" dataKey="visits" name="Ziyaret" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                  <Line yAxisId="right" type="monotone" dataKey="revenue" name="Ciro (₺)" stroke="#10b981" strokeWidth={2} dot={{ fill: '#10b981' }} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>

            <div className="bg-white bg-opacity-5 rounded-xl p-4">
              <h3 className="text-sm font-semibold mb-4 flex items-center gap-2">
                <Package className="w-4 h-4" />
                Satılan Ürün Grupları
              </h3>
              {performanceData.product_groups.length > 0 ? (
                <div className="space-y-2 max-h-[250px] overflow-y-auto">
                  {performanceData.product_groups.map((product, index) => (
                    <div key={index} className="flex items-start justify-between bg-white bg-opacity-5 rounded-lg p-2 gap-2">
                      <div className="flex items-start gap-2 flex-1 min-w-0">
                        <div 
                          className="w-3 h-3 rounded-full flex-shrink-0 mt-0.5" 
                          style={{ backgroundColor: COLORS[index % COLORS.length] }}
                        />
                        <span className="text-xs break-words" title={product.name}>{product.name}</span>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <span className="text-xs font-semibold">{product.count} adet</span>
                        <span className="text-xs text-slate-400 ml-2">₺{product.revenue.toLocaleString('tr-TR')}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center text-slate-400 py-8 text-sm">
                  Bu dönemde satış yok
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* GRAFİKLER */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Ürün Bazlı Şirket Karı */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-6 flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-blue-600" />
            Ürün Bazlı Şirket Karı
          </h3>
          {productChartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={productChartData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis type="number" tick={{ fontSize: 12 }} />
                <YAxis dataKey="name" type="category" tick={{ fontSize: 10 }} width={120} />
                <Tooltip formatter={(value) => `₺${value.toLocaleString('tr-TR')}`} />
                <Bar dataKey="Şirket Karı" fill="#3b82f6" radius={[0, 8, 8, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-80 flex items-center justify-center text-gray-500">
              Veri bulunamadı
            </div>
          )}
        </div>

        {/* Temsilci Bazlı Ciro ve Puan */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-6 flex items-center gap-2">
            <Users className="w-5 h-5 text-green-600" />
            Temsilci Performans Karşılaştırması
          </h3>
          {representativeChartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={representativeChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip formatter={(value) => `₺${value.toLocaleString('tr-TR')}`} />
                <Legend />
                <Bar dataKey="Ciro" fill="#10b981" radius={[8, 8, 0, 0]} />
                <Bar dataKey="Puan" fill="#8b5cf6" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-80 flex items-center justify-center text-gray-500">
              Veri bulunamadı
            </div>
          )}
        </div>
      </div>

      {/* Müşteri Satış Analizi Kısayol */}
      <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl shadow-lg p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-xl font-bold mb-2">Müşteri Satış Analizi</h3>
            <p className="text-white/90 text-sm">Yeni ve mevcut müşteri satış detaylarını görüntüleyin</p>
          </div>
          <Button
            onClick={() => navigate('/customer-sales-report')}
            className="bg-white text-indigo-600 hover:bg-gray-100"
          >
            Raporu Görüntüle
          </Button>
        </div>
      </div>

      {/* DETAY DİALOG - Ziyaret, Satış vb. */}
      <Dialog open={showRepDetailsDialog} onOpenChange={setShowRepDetailsDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {repDetailsData.rep} - {
                repDetailsData.type === 'visits' ? 'Ziyaretler' :
                repDetailsData.type === 'arvento' ? 'Cihaz Satışları' :
                repDetailsData.type === 'calls' ? 'Aramalar' :
                repDetailsData.type === 'camera' ? 'Kamera Satışları' :
                repDetailsData.type === 'sensors' ? 'Sensör Satışları' :
                repDetailsData.type === 'm2m' ? 'M2M Satışları' : ''
              }
            </DialogTitle>
            <DialogDescription>
              Seçili dönem için detaylı kayıtlar
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4">
            {repDetailsData.data.length === 0 ? (
              <p className="text-center text-gray-500 py-8">Veri bulunamadı</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Tarih</th>
                      <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Müşteri</th>
                      {(repDetailsData.type === 'arvento' || repDetailsData.type === 'camera' || repDetailsData.type === 'sensors' || repDetailsData.type === 'm2m') && (
                        <>
                          <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Ürün</th>
                          <th className="px-4 py-2 text-center text-xs font-semibold text-gray-600">Adet</th>
                        </>
                      )}
                      <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Notlar</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {repDetailsData.data.map((item, idx) => (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="px-4 py-2 text-sm">
                          {item.date ? format(new Date(item.date), 'dd MMM yyyy HH:mm', { locale: tr }) : '-'}
                        </td>
                        <td className="px-4 py-2 text-sm font-medium">{item.customer_name || item.customer || '-'}</td>
                        {(repDetailsData.type === 'arvento' || repDetailsData.type === 'camera' || repDetailsData.type === 'sensors' || repDetailsData.type === 'm2m') && (
                          <>
                            <td className="px-4 py-2 text-sm">{item.product_name || item.product || '-'}</td>
                            <td className="px-4 py-2 text-sm text-center">
                              <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium">
                                {item.quantity || 0}
                              </span>
                            </td>
                          </>
                        )}
                        <td className="px-4 py-2 text-sm text-gray-600">{item.notes || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* DETAYLI SATIŞ RAPORU MODAL */}
      <Dialog open={showDetailedSalesModal} onOpenChange={setShowDetailedSalesModal}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShoppingCart className="w-5 h-5 text-emerald-600" />
              Detaylı Satış Raporu
              <span className="text-sm font-normal text-gray-500 ml-2">
                ({format(new Date(startDate), 'd MMM yyyy', { locale: tr })} - {format(new Date(endDate), 'd MMM yyyy', { locale: tr })})
              </span>
            </DialogTitle>
            <DialogDescription>
              Kategori, ürün, müşteri ve tarih bilgilerini içeren tam satış listesi
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4">
            {detailedSalesLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
              </div>
            ) : detailedSalesData.length === 0 ? (
              <p className="text-center text-gray-500 py-8">Bu dönemde satış bulunamadı</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="detailed-sales-table">
                  <thead className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-semibold">Tarih</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold">Müşteri</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold">Temsilci</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold">Kategori</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold">Ürün</th>
                      <th className="px-3 py-2 text-center text-xs font-semibold">Adet</th>
                      <th className="px-3 py-2 text-right text-xs font-semibold">Birim Fiyat</th>
                      <th className="px-3 py-2 text-right text-xs font-semibold">Toplam</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {detailedSalesData.map((item, idx) => (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="px-3 py-2 text-xs whitespace-nowrap">
                          {item.date ? format(new Date(item.date), 'dd.MM.yyyy', { locale: tr }) : '-'}
                        </td>
                        <td className="px-3 py-2 text-xs font-medium max-w-[150px] truncate" title={item.customer}>
                          {item.customer || '-'}
                        </td>
                        <td className="px-3 py-2 text-xs">{item.representative || '-'}</td>
                        <td className="px-3 py-2 text-xs">
                          <span className="bg-gray-100 text-gray-700 px-2 py-0.5 rounded text-xs">
                            {item.category || 'Diğer'}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-xs max-w-[200px] truncate" title={item.product}>
                          {item.product || '-'}
                        </td>
                        <td className="px-3 py-2 text-xs text-center">
                          <span className="bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full font-semibold">
                            {item.quantity || 0}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-xs text-right">
                          ₺{(item.unit_price || 0).toLocaleString('tr-TR')}
                        </td>
                        <td className="px-3 py-2 text-xs text-right font-semibold text-emerald-700">
                          ₺{(item.total || 0).toLocaleString('tr-TR')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-gray-100 sticky bottom-0">
                    <tr className="font-bold">
                      <td colSpan="5" className="px-3 py-2 text-sm">TOPLAM</td>
                      <td className="px-3 py-2 text-sm text-center">
                        {detailedSalesData.reduce((sum, item) => sum + (item.quantity || 0), 0)}
                      </td>
                      <td className="px-3 py-2 text-sm text-right">-</td>
                      <td className="px-3 py-2 text-sm text-right text-emerald-700">
                        ₺{detailedSalesData.reduce((sum, item) => sum + (item.total || 0), 0).toLocaleString('tr-TR')}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Reports;
